import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Users, Activity, CheckCircle2, Flame, ShieldCheck, ShieldOff,
  KeyRound, Trash2, Loader2, Crown, Gift,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [compTarget, setCompTarget] = useState(null);

  const load = () => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/users")])
      .then(([s, u]) => { setStats(s.data); setUsers(u.data); })
      .catch(() => toast.error("Failed to load admin data"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleRole = async (u) => {
    const role = u.role === "admin" ? "user" : "admin";
    try {
      await api.post(`/admin/users/${u.id}/role`, { role });
      toast.success(`${u.name || u.email} is now ${role}`);
      setUsers((list) => list.map((x) => x.id === u.id ? { ...x, role } : x));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success("User deleted");
      setUsers((list) => list.filter((x) => x.id !== deleteTarget.id));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setDeleteTarget(null);
  };

  const statCards = stats ? [
    { icon: <Users className="w-5 h-5 text-blue-400" />, label: "Users", value: stats.total_users },
    { icon: <Activity className="w-5 h-5 text-emerald-400" />, label: "Active Today", value: stats.active_today },
    { icon: <CheckCircle2 className="w-5 h-5 text-teal-400" />, label: "Completions", value: stats.total_completions },
    { icon: <Flame className="w-5 h-5 text-orange-500" />, label: "Habits", value: stats.total_habits },
    { icon: <Crown className="w-5 h-5 text-amber-400" />, label: "Admins", value: stats.admins },
  ] : [];

  return (
    <div className="space-y-8 pb-24 md:pb-8" data-testid="admin-page">
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-emerald-400 mb-2 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" /> Admin
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">Control Center</h1>
        <p className="text-white mt-2">Platform overview and user management.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {statCards.map((s, i) => (
          <div key={i} data-testid={`admin-stat-${i}`} className="p-4 rounded-2xl border border-[#242417] bg-[#12141C]">
            <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center mb-3">{s.icon}</div>
            <div className="font-mono-stat text-2xl font-bold">{s.value}</div>
            <div className="text-xs text-white mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-[#242417] bg-[#12141C] shadow-xl overflow-hidden">
        <div className="p-5 border-b border-[#242417] flex items-center gap-2">
          <Users className="w-5 h-5 text-emerald-400" />
          <h2 className="font-display text-lg font-bold">All Users ({users.length})</h2>
        </div>

        {loading ? (
          <div className="p-10 text-center text-white">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="text-left text-white border-b border-[#242417]">
                  <th className="p-4 font-medium">User</th>
                  <th className="p-4 font-medium">Role</th>
                  <th className="p-4 font-medium">Level</th>
                  <th className="p-4 font-medium hidden sm:table-cell">Streak</th>
                  <th className="p-4 font-medium hidden sm:table-cell">Habits</th>
                  <th className="p-4 font-medium hidden md:table-cell">Done</th>
                  <th className="p-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <motion.tr
                    key={u.id}
                    data-testid={`admin-user-row-${u.id}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border-b border-[#242417]/40 hover:bg-white/[0.02]"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center text-black font-bold shrink-0">
                          {(u.name || u.email)[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{u.name} {u.is_me && <span className="text-xs text-emerald-400">(you)</span>}</div>
                          <div className="text-xs text-white truncate">{u.email}</div>
                          <AccessBadge u={u} />
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded-full border ${u.role === "admin" ? "bg-amber-500/10 text-amber-400 border-amber-500/30" : "bg-white/5 text-white border-[#33331f]"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="p-4 font-mono-stat">{u.level}</td>
                    <td className="p-4 font-mono-stat hidden sm:table-cell">{u.best_streak}</td>
                    <td className="p-4 font-mono-stat hidden sm:table-cell">{u.habit_count}</td>
                    <td className="p-4 font-mono-stat hidden md:table-cell">{u.completions}</td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`admin-role-toggle-${u.id}`}
                          onClick={() => toggleRole(u)}
                          disabled={u.is_me}
                          title={u.role === "admin" ? "Demote to user" : "Promote to admin"}
                          className="p-2 rounded-lg text-white hover:bg-white/5 disabled:opacity-30"
                        >
                          {u.role === "admin" ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                        </button>
                        <button
                          data-testid={`admin-comp-${u.id}`}
                          onClick={() => setCompTarget(u)}
                          title="Grant complimentary access"
                          className="p-2 rounded-lg text-white hover:text-amber-400 hover:bg-amber-500/10"
                        >
                          <Gift className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`admin-reset-pw-${u.id}`}
                          onClick={() => setResetTarget(u)}
                          title="Reset password"
                          className="p-2 rounded-lg text-white hover:text-emerald-400 hover:bg-white/5"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`admin-delete-${u.id}`}
                          onClick={() => setDeleteTarget(u)}
                          disabled={u.is_me}
                          title="Delete user"
                          className="p-2 rounded-lg text-white hover:text-red-400 hover:bg-red-500/10 disabled:opacity-30"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ResetPasswordModal target={resetTarget} onClose={() => setResetTarget(null)} />
      <CompModal target={compTarget} onClose={() => setCompTarget(null)} onDone={load} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#12141C] border-[#242417] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Delete {deleteTarget?.name || deleteTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription className="text-white">
              This permanently deletes the user and all their habits, entries, journal and challenge memberships. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#33331f] text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-confirm-delete" onClick={doDelete} className="bg-red-500 hover:bg-red-600 text-white">Delete User</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ResetPasswordModal({ target, onClose }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPw(""); }, [target]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      await api.post(`/admin/users/${target.id}/reset-password`, { new_password: pw });
      toast.success("Password reset");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#12141C] border-[#242417] text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Reset password</DialogTitle>
          <DialogDescription className="text-white">Set a new password for {target?.email}.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <input
            data-testid="admin-new-password-input"
            type="text"
            autoFocus
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="New password (min 6 chars)"
            className="w-full h-11 px-4 rounded-xl bg-[#0D0F17] border border-[#242417] outline-none focus:border-emerald-400 text-white"
          />
          <button data-testid="admin-reset-submit" type="submit" disabled={saving} className="w-full h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-400 text-black font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Set Password
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function AccessBadge({ u }) {
  let label = "Free", cls = "bg-white/5 text-white border-[#33331f]";
  if (u.role === "admin") { label = "Admin"; cls = "bg-amber-500/10 text-amber-400 border-amber-500/30"; }
  else if (u.comp_granted && u.is_premium) { label = "Complimentary"; cls = "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30"; }
  else if (u.is_premium) { label = "Paid"; cls = "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"; }
  else if (u.on_trial) { label = `On trial · ${u.trial_days_left}d`; cls = "bg-blue-500/10 text-blue-300 border-blue-500/30"; }
  else if (u.trial_expired) { label = "Trial expired"; cls = "bg-red-500/10 text-red-300 border-red-500/30"; }
  return (
    <span data-testid={`admin-access-badge-${u.id}`} className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>
      {label}
    </span>
  );
}

function CompModal({ target, onClose, onDone }) {
  const [days, setDays] = useState(30);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDays(30); }, [target]);

  const submit = async (e) => {
    e.preventDefault();
    const n = parseInt(days, 10);
    if (!n || n < 1) return toast.error("Enter a number of days (1 or more)");
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/comp`, { days: n });
      const until = new Date(data.premium_until).toLocaleDateString();
      toast.success(`Granted ${n} complimentary day${n > 1 ? "s" : ""} — access until ${until}`);
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#12141C] border-[#242417] text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-400" /> Complimentary access
          </DialogTitle>
          <DialogDescription className="text-white">
            Grant free access to {target?.name || target?.email}. This extends their access from today (or their current end date if later).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="flex flex-wrap gap-2">
            {[7, 30, 90, 365].map((d) => (
              <button
                type="button"
                key={d}
                data-testid={`admin-comp-preset-${d}`}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-sm border ${parseInt(days, 10) === d ? "bg-amber-500/20 border-amber-500/40 text-amber-300" : "border-[#33331f] text-white hover:bg-white/5"}`}
              >
                {d === 365 ? "1 year" : `${d} days`}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-white mb-2">Days</label>
            <input
              data-testid="admin-comp-days-input"
              type="number"
              min={1}
              max={3650}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-full h-11 px-4 rounded-xl bg-[#0D0F17] border border-[#242417] outline-none focus:border-amber-400 text-white"
            />
          </div>
          <button data-testid="admin-comp-submit" type="submit" disabled={saving} className="w-full h-11 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-400 text-black font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Grant Access
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
